import type { Session } from '@/features/sessions/types';
import type { ClientExercise } from '@/features/exercises/types';
import type { AdminSurface } from '@/lib/guards';
import { surfacePaths } from '@/lib/paths';

import type { CopingTypeChange } from '../hooks/useCopingTypes';
import type { ClientAssessment, RedFlag } from '../types';
import type { TimelineEvent, TimelineEventTone } from '../types/history';

interface MergeTimelineInput {
  clientId: string;
  sessions?: Session[];
  redFlags?: RedFlag[];
  assessments?: ClientAssessment[];
  exercises?: ClientExercise[];
  copingTypeChanges?: CopingTypeChange[];
  fromISO: string;
}

const sessionToneByStatus: Record<string, TimelineEventTone> = {
  completed: 'success',
  in_progress: 'neutral',
  scheduled: 'neutral',
  cancelled: 'warning',
  no_show: 'warning',
  rescheduled: 'neutral',
};

const exerciseToneByStatus: Record<string, TimelineEventTone> = {
  pending: 'info',
  in_progress: 'info',
  completed: 'success',
  reviewed: 'success',
};

function mapSession(s: Session, linkTo: string): TimelineEvent {
  const statusLabel = s.status.replace('_', ' ');
  return {
    id: `session:${s.id}`,
    kind: 'session',
    // BE schema guarantees scheduledStart; no fallback to createdAt needed.
    at: s.scheduledStart,
    title: `Session ${statusLabel}`,
    subtitle: s.practitionerName ?? undefined,
    tone: sessionToneByStatus[s.status] ?? 'neutral',
    linkTo,
    linkParams: { sessionId: s.id },
  };
}

function mapRedFlag(f: RedFlag, linkTo: string): TimelineEvent {
  const statusTitle =
    f.status === 'open'
      ? 'Red flag raised'
      : f.status === 'under_review'
        ? 'Red flag under review'
        : 'Red flag closed';
  // Surface the raiser's name (and on closed flags, the resolver's name) via
  // `actors`. `authorName` carries the raiser since the FE bubble treats it
  // as the row's primary actor; `reviewerName` carries the resolver so
  // closed flags can read "Raised by X · Resolved by Y" when the BE join
  // ships both names.
  const raisedByName = f.raisedByName ?? undefined;
  const resolvedByName = f.resolvedByName ?? undefined;
  const hasActors =
    (raisedByName !== undefined && raisedByName !== '') ||
    (resolvedByName !== undefined && resolvedByName !== '');
  return {
    id: `red-flag:${f.id}`,
    kind: 'red-flag',
    at: f.createdAt,
    title: statusTitle,
    subtitle: f.flagType.replace('_', ' '),
    tone: 'danger',
    linkTo,
    linkParams: { id: f.id },
    actors: hasActors ? { authorName: raisedByName, reviewerName: resolvedByName } : undefined,
  };
}

function mapAssessment(a: ClientAssessment, clientId: string, linkTo: string): TimelineEvent {
  const statusLabel = a.status.replace('_', ' ');
  // Carry IDs + status so the bubble can branch: completed → assignment result
  // detail; pending/in_progress → assignments-list filtered to this client.
  return {
    id: `assessment:${a.id}`,
    kind: 'assessment',
    at: a.createdAt,
    title: `Assessment ${statusLabel}: ${a.title}`,
    subtitle: a.templateCode ?? undefined,
    tone: a.status === 'completed' ? 'success' : 'info',
    linkTo,
    linkParams: {
      clientId,
      assignmentId: a.id,
      assessmentId: a.assessmentId,
      status: a.status,
    },
    linkSearch: { tab: 'assessments' },
  };
}

function mapExercise(e: ClientExercise, clientId: string, linkTo: string): TimelineEvent {
  const statusLabel = e.status.replace('_', ' ');
  return {
    id: `exercise:${e.id}`,
    kind: 'exercise',
    at: e.createdAt,
    title: `Exercise ${statusLabel}`,
    subtitle: e.exerciseTitle,
    tone: exerciseToneByStatus[e.status] ?? 'info',
    linkTo,
    linkParams: { clientId, clientExerciseId: e.id },
  };
}

const COPING_CHANGE_TITLE: Record<CopingTypeChange['source'], string> = {
  session: 'Coping updated',
  chat_session: 'Coping updated in chat',
  profile: 'Coping updated in the profile',
};

function mapCopingTypeChange(change: CopingTypeChange): TimelineEvent {
  return {
    id: `coping-change:${change.id}`,
    kind: 'coping-change',
    at: change.createdAt,
    title: COPING_CHANGE_TITLE[change.source],
    // A session-sourced change nests under that session in the feed.
    linkParams: change.sessionId ? { sessionId: change.sessionId } : undefined,
    actors: { authorName: change.changedByName },
    copingChange: {
      previousCopingTypes: change.previousCopingTypes,
      copingTypes: change.copingTypes,
    },
  };
}

export function mergeTimeline(input: MergeTimelineInput, surface: AdminSurface): TimelineEvent[] {
  const {
    clientId,
    sessions = [],
    redFlags = [],
    assessments = [],
    exercises = [],
    copingTypeChanges = [],
    fromISO,
  } = input;

  const paths = surfacePaths(surface);
  // Keep placeholder tokens (`$sessionId` etc.) — downstream consumers treat these as TanStack
  // Router path templates and substitute params at render time.
  const sessionLinkTo = paths.sessions.detail('$sessionId');
  const redFlagLinkTo = paths.redFlags.detail('$id');
  const clientLinkTo = paths.clients.detail('$clientId');
  const exerciseLinkTo = paths.clients.exerciseReview('$clientId', '$clientExerciseId');

  const events: TimelineEvent[] = [
    ...sessions.map((s) => mapSession(s, sessionLinkTo)),
    ...redFlags.map((f) => mapRedFlag(f, redFlagLinkTo)),
    ...assessments.map((a) => mapAssessment(a, clientId, clientLinkTo)),
    ...exercises.map((e) => mapExercise(e, clientId, exerciseLinkTo)),
    ...copingTypeChanges.map(mapCopingTypeChange),
  ];

  return events
    .filter((e) => e.at >= fromISO)
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}
